import React, { useEffect, useState } from 'react';
import * as _ from 'lodash';
import Loading from 'common/ui/Loading';
import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import ValidationError from 'common/validationError';
import PartnerIntegration from 'domain/partnerIntegration';
import { getPartnerName, Partner } from 'features/settings/importMapping/partner';
import ManagePartnerIntegration from './ManagePartnerIntegration';
import partnerIntegrationService, { CreatePartnerIntegrationRequest } from './partnerIntegrationService';
import ErpIntegration from 'domain/erpIntegration';
import ManageErpIntegration from './ManageErpIntegration';
import ManageBidIntegration from './ManageBidIntegration';
import BidIntegration from './bidIntegration';
import DeleteConfirmationModal, { useDeleteConfirmationModal } from 'common/ui/DeleteConfirmationModal';
import MerchantDetailsComponentProps from '../merchantDetailsComponentProps';
import './PartnerSettings.scss'

const partners = [Partner.AgTrax, Partner.DTN, Partner.Oakland, Partner.Agris, Partner.Barchart, Partner.Agvance, Partner.EverAg, Partner.AgVantage, Partner.AgVision, Partner.AgTech, Partner.Intellego];

type DeleteDetails = {title: string, text: React.ReactNode, onConfirm: (() => Promise<any>) };

const PartnerSettings: React.FC<MerchantDetailsComponentProps> = props => {

    const merchantId = props.merchant.id;

    const [isRetrieved, setIsRetrieved] = useState(false);
    const [partnerIntegrations, setPartnerIntegrations] = useState<PartnerIntegration[]>(null);
    const [erpIntegration, setErpIntegration] = useState<ErpIntegration>(null);
    const [bidIntegration, setBidIntegration] = useState<BidIntegration>(null);

    const deleteConfirmation = useDeleteConfirmationModal();
    const [deleteDetails, setDeleteDetails] = useState<DeleteDetails>(null);

    useEffect(() => {
        if (props.merchant == null) return;

        Promise.all([
                partnerIntegrationService.listPartnerIntegrations(merchantId)
                    .then(m =>setPartnerIntegrations(m)),
                partnerIntegrationService.getErpIntegration(merchantId)
                    .then(m => setErpIntegration(m)),
                partnerIntegrationService.getBidIntegration(merchantId)
                    .then(m => setBidIntegration(m))
            ])
            .finally(() => {
                setIsRetrieved(true);
            });
    }, [props.merchant]);

    async function handleCreateIntegration(partner: Partner, request: CreatePartnerIntegrationRequest) {
        const newIntegration = await partnerIntegrationService.createPartnerIntegration(props.merchant.id, {
            ...request,
            partner: partner
        });

        setPartnerIntegrations([...partnerIntegrations, newIntegration]);
    }

    async function handleUpdateIntegration(integration: PartnerIntegration) {
        const previousIntegrations = partnerIntegrations;
        try {
            setPartnerIntegrations(partnerIntegrations.map(m => m.partner === integration.partner
                ? integration
                : m));
            await partnerIntegrationService.updatePartnerIntegration(props.merchant.id, integration.partner, {
                authenticationKey: integration.authenticationKey,
                configuration: integration.configuration
            });

            if(integration.partner === Partner.Agvance) {
                partnerIntegrationService.getErpIntegration(merchantId)
                    .then(m => setErpIntegration(m));
            }
        }
        catch (err) {
            setPartnerIntegrations(previousIntegrations);
            loggingService.error("Unable to update partner integration", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to update partner integration");
        }
    }

    async function handleRemoveIntegration(partner: Partner) {
        if (erpIntegration?.partner === partner || bidIntegration?.partner === partner) {
            notificationService.error("Cannot remove partner with active integrations");
            return;
        }

        setDeleteDetails({
            title: "Delete Partner Configuration",
            text: <div>Are you sure you want to remove the {getPartnerName(partner)} configuration? This will remove all data mappings!</div>,
            onConfirm: async () => {
                try {
                    await partnerIntegrationService.deletePartnerIntegration(merchantId, partner);
                    setPartnerIntegrations(partnerIntegrations.filter(m => m.partner !== partner));
                    notificationService.success(`Removed ${getPartnerName(partner)} configuration`);
                }
                catch (err) {
                    const partnerName = getPartnerName(partner);
                    loggingService.error(`Unable to delete ${partnerName} configuration`, err as Error);
                    const validationError = err as ValidationError;
                    notificationService.error(validationError.errors?.join(", ") ?? `Unable to delete ${partnerName} configuration`);
                }
            }
        });
        deleteConfirmation.show();
    }

    async function handleUpdateErpIntegration(integration: ErpIntegration) {
        try {
            await partnerIntegrationService.updateErpIntegration(props.merchant.id, integration);
            setErpIntegration({...integration, merchantId: props.merchant.id});
        }
        catch (err) {
            loggingService.error("Unable to update ERP integration", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to update ERP integration");
        }
    }

    async function handleRemoveErpIntegration() {
        setDeleteDetails({
            title: "Delete ERP Integration",
            text: "Are you sure you want to remove this ERP integration?",
            onConfirm: async () => {
                await partnerIntegrationService.deleteErpIntegration(merchantId);
                notificationService.success(`Removed ${getPartnerName(erpIntegration.partner)} ERP integration`);
                setErpIntegration(null);
            }
        });
        deleteConfirmation.show();
    }

    async function handleUpdateBidIntegration(integration: BidIntegration) {
        try {
            await partnerIntegrationService.updateBidIntegration(props.merchant.id, integration);
            setBidIntegration({...integration, merchantId: props.merchant.id});
        }
        catch (err) {
            loggingService.error("Unable to update bid integration", err as Error);
            const validationError = err as ValidationError;
            notificationService.error(validationError.errors?.join(", ") ?? "Unable to update bid integration");
        }
    }

    async function handleRemoveBidIntegration() {
        setDeleteDetails({
            title: "Delete Bid Integration",
            text: "Are you sure you want to remove this bid integration?",
            onConfirm: async () => {
                await partnerIntegrationService.deleteBidIntegration(merchantId);
                notificationService.success(`Removed ${getPartnerName(bidIntegration.partner)} cash bid integration`);
                setBidIntegration(null);
            }
        });
        deleteConfirmation.show();
    }

    if (!isRetrieved) return <Loading />;

    return <div className="partner-settings row">
        <div className="col-4">
            <h3>Partners</h3>

            {/* Display configured partners first */}
            {_.orderBy(partners, m => partnerIntegrations.some(pi => pi.partner === m), "desc")
                .map(m => <div key={m} className="">
                    <ManagePartnerIntegration 
                        partner={m} 
                        integration={partnerIntegrations.find(pi => pi.partner === m)}
                        onCreate={req => handleCreateIntegration(m, req)}
                        onUpdate={handleUpdateIntegration}
                        onRemove={() => handleRemoveIntegration(m)}
                    />
                </div>)}
        </div>
        <div className="col-4">
            <h3>Integrations</h3>


            {partnerIntegrations.length > 0 && <>
                <ManageErpIntegration erpIntegration={erpIntegration}
                    partners={partnerIntegrations.map(m => m.partner)}
                    onSave={handleUpdateErpIntegration}
                    onRemove={handleRemoveErpIntegration}  />

                <ManageBidIntegration bidIntegration={bidIntegration}
                    partners={partnerIntegrations.map(m => m.partner)}
                    onSave={handleUpdateBidIntegration}
                    onRemove={handleRemoveBidIntegration} />
            </>}

            {partnerIntegrations.length === 0 && <div>You must configure a partner before adding an integration</div>}
        </div>

        <DeleteConfirmationModal controller={deleteConfirmation}
            title={deleteDetails?.title}
            text={deleteDetails?.text}
            onConfirm={deleteDetails?.onConfirm} />
    </div>;
}



export default PartnerSettings;
